import { DateTime } from "luxon";

const MIN_ISO_DATE_LENGTH = 10;

export interface ParseDateOptions {
  /**
   * Object keys whose value is kept verbatim — neither coerced nor walked. For fields whose content may
   * look like a date but is contractually text (a raw imported "1983-06-24" shown as-is).
   */
  ignoreKeys?: readonly string[];
}

/**
 * Walks a JSON body and revives every string Luxon's `fromISO` accepts (10+ chars) into a `Date`. Shape-based:
 * it cannot tell a date field from a string that merely looks like one, so exempt the latter via `ignoreKeys`.
 */
export const parseDate = <T>(data: unknown, options?: ParseDateOptions) => {
  const ignoredKeys = new Set(options?.ignoreKeys);

  const recursiveParse = (value: unknown): unknown => {
    if (typeof value === "string" && value.length >= MIN_ISO_DATE_LENGTH && DateTime.fromISO(value).isValid) {
      return DateTime.fromISO(value).toJSDate();
    }

    if (Array.isArray(value)) {
      return value.map(val => recursiveParse(val));
    }

    if (typeof value === "object" && value !== null) {
      return Object.fromEntries(
        Object.entries(value).map(([key, val]) => [key, ignoredKeys.has(key) ? val : recursiveParse(val)])
      );
    }

    return value;
  };

  return recursiveParse(data) as T;
};
